import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  ControlGroup,
  Dialog,
  EmptyInputGroup,
  FormField,
  Grid,
  Heading,
  majorScale,
  NumberInput,
} from '../../npaper';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import {
  IBOMItem,
  IBOMItemSnapshotIn,
  IProduct,
  ProductType,
  Unit,
  useStores,
} from '../../stores';
import { ProductSelect } from './ProductSelect';
import { ProductSupplierSelect } from './ProductSupplierSelect';

export const BOMDialog: React.FC<{
  isShown: boolean;
  onClose: (item?: IBOMItem) => void;
  bomItem?: IBOMItem;
  product: IProduct;
}> = ({ onClose, bomItem, product, ...props }) => {
  const [isShown, setIsShown] = useState(props.isShown);
  const [created, setCreated] = useState<IBOMItem>();

  const [selectedProduct, setSelectedProduct] = useState<IProduct>();

  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  const close = () => {
    setIsShown(false);
  };

  const { bomItemStore, productStore } = useStores();
  const { watch, control, errors, reset, handleSubmit, formState, setValue } =
    useForm<IBOMItemSnapshotIn>();

  useEffect(() => {
    if (!isShown) return;
    if (bomItem) {
      reset(getSnapshot(bomItem));
    } else {
      reset({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bomItem, isShown]);

  const onSubmit = async (values: IBOMItemSnapshotIn) => {
    if (bomItem) {
      // Update
      try {
        await bomItemStore.updateItem(bomItem, values);
        await productStore.getItem(product.id, true);
        close();
      } catch (err) {
        // TODO: Handle error
        throw err;
      }
    } else {
      // Create new item
      try {
        const item = await bomItemStore.createItem({
          ...values,
          parent: product.id,
        });
        await productStore.getItem(product.id, true);
        setCreated(item);
        close();
      } catch (err) {
        // TODO: Handle error
        throw err;
      }
    }
  };

  const selectedProductId = watch('product');

  useEffect(() => {
    if (!selectedProductId) return;
    let isSub = true;
    productStore
      .getItem(selectedProductId)
      .then((p) => isSub && setSelectedProduct(p));
    return () => {
      isSub = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId]);

  return (
    <Dialog
      title={bomItem ? 'Rediger stykliste' : 'Tilføj til styklisten'}
      isShown={isShown}
      onClose={() => onClose(created)}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            Annuller
          </Button>
          <Button
            primary
            isLoading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {bomItem ? 'Gem ændringer' : 'Tilføj produkt'}
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <Heading size={300} className="col-span-12">
          Detaljer
        </Heading>

        <FormField
          className="col-span-9"
          label="Produkt"
          inline
          isInvalid={!!errors.product}
          hint={errors.product && errors.product.message}
        >
          <Controller
            control={control}
            name="product"
            defaultValue={null}
            rules={{ required: 'Du skal vælge et produkt' }}
            render={({ onChange, value }) => (
              <ProductSelect
                disabled={!!bomItem}
                onSelect={(p) => {
                  onChange(p.id);
                  setValue('default_supplier_item', null);
                }}
                selected={value}
                filter={{
                  type__neq: ProductType.PACKAGE,
                }}
              />
            )}
          />
        </FormField>

        <FormField className="col-span-9" label="Antal" inline>
          <ControlGroup>
            <Controller
              control={control}
              name="quantity"
              defaultValue={1}
              render={({ onChange, value, onBlur }) => (
                <NumberInput
                  placeholder="Antal i pakkeløsningen"
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                />
              )}
            />
            <EmptyInputGroup>
              <FormattedUnit
                unit={selectedProduct ? selectedProduct.unit : Unit.PCS}
                unitDisplay="long"
              />
            </EmptyInputGroup>
          </ControlGroup>
        </FormField>

        <FormField
          className="col-span-9"
          label="Standard leverandør"
          inline
          display={
            selectedProduct && selectedProduct.type === ProductType.GOOD
              ? 'flex'
              : 'none'
          }
        >
          <Controller
            control={control}
            name="default_supplier_item"
            defaultValue={null}
            render={({ onChange, value }) => (
              <ProductSupplierSelect
                disabled={!selectedProduct}
                onSelectSupplierProductItem={(item) => onChange(item.id)}
                value={value}
                filter={{
                  product: selectedProduct ? selectedProduct.id : undefined,
                }}
              />
            )}
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};
